import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Select,
  Space, Tag, message, Popconfirm, Descriptions, DatePicker, Divider, Badge
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  KeyOutlined, GlobalOutlined, TeamOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ExclamationCircleOutlined, CopyOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { apiClient } from '@/services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface Agent {
  id: string;
  name: string;
  code: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  company?: string;
  address?: string;
  status: string;
  licenseKey: string;
  maxDomains: number;
  expiresAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  domainLicenses?: DomainLicense[];
  _count?: {
    domainLicenses: number;
  };
}

interface DomainLicense {
  id: string;
  agentId: string;
  domain: string;
  subdomain?: string;
  fullDomain: string;
  status: string;
  licenseKey: string;
  expiresAt?: string;
  activatedAt?: string;
  lastVerifiedAt?: string;
  ipWhitelist?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const AgentManagement: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [agentModalVisible, setAgentModalVisible] = useState(false);
  const [domainModalVisible, setDomainModalVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [selectedLicense, setSelectedLicense] = useState<DomainLicense | null>(null);
  const [agentForm] = Form.useForm();
  const [domainForm] = Form.useForm();

  // 复制密钥到剪贴板
  const copyToClipboard = (text: string, label: string = '密钥') => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label}已复制到剪贴板`);
    }).catch(() => {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`${label}已复制到剪贴板`);
      } catch (err) {
        message.error('复制失败，请手动复制');
      }
      document.body.removeChild(textArea);
    });
  };

  // 加载代理商列表
  const loadAgents = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/agents');
      if (res?.success) {
        setAgents(res.data || []);
      }
    } catch (error: any) {
      message.error('加载代理商列表失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, []);

  // 创建/编辑代理商
  const handleAgentSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null
      };

      if (selectedAgent) {
        // 更新
        await apiClient.put(`/admin/agents/${selectedAgent.id}`, data);
        message.success('代理商更新成功');
      } else {
        // 创建
        await apiClient.post('/admin/agents', data);
        message.success('代理商创建成功');
      }

      setAgentModalVisible(false);
      setSelectedAgent(null);
      agentForm.resetFields();
      loadAgents();
    } catch (error: any) {
      message.error('操作失败: ' + (error.message || '未知错误'));
    }
  };

  // 删除代理商
  const handleDeleteAgent = async (id: string) => {
    try {
      const res = await apiClient.delete(`/admin/agents/${id}`);
      if (res?.success) {
        message.success(res.message || '代理商删除成功');
        loadAgents();
      } else {
        message.error('删除失败: ' + (res?.message || '未知错误'));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          '删除失败，请检查网络连接或联系管理员';
      message.error('删除失败: ' + errorMessage);
      console.error('删除代理商失败:', error);
    }
  };

  // 重置授权密钥
  const handleResetLicenseKey = async (id: string) => {
    try {
      const res = await apiClient.post(`/admin/agents/${id}/reset-license-key`);
      if (res?.success) {
        message.success('授权密钥已重置: ' + res.data.licenseKey);
        loadAgents();
      }
    } catch (error: any) {
      message.error('重置失败: ' + (error.message || '未知错误'));
    }
  };

  // 打开域名授权模态框
  const handleAddDomain = (agent: Agent) => {
    setSelectedAgent(agent);
    setSelectedLicense(null);
    domainForm.resetFields();
    setDomainModalVisible(true);
  };

  // 编辑域名授权
  const handleEditDomain = (license: DomainLicense, agent: Agent) => {
    setSelectedAgent(agent);
    setSelectedLicense(license);
    let ipWhitelist = [];
    if (license.ipWhitelist) {
      try {
        ipWhitelist = JSON.parse(license.ipWhitelist);
      } catch {
        ipWhitelist = [];
      }
    }
    
    // 从 fullDomain 解析子域名（如果 subdomain 字段为空）
    let subdomain = license.subdomain;
    let domain = license.domain;
    
    // 如果 subdomain 为空但 fullDomain 包含子域名，尝试解析
    if (!subdomain && license.fullDomain && license.domain) {
      // 例如：fullDomain = "api.duo123.net", domain = "duo123.net"
      // 那么 subdomain = "api"
      if (license.fullDomain.endsWith('.' + license.domain) || license.fullDomain === license.domain) {
        // fullDomain 以 domain 结尾或者是完整的 domain
        if (license.fullDomain === license.domain) {
          // 完全相同，说明是主域名，没有子域名
          subdomain = undefined;
        } else {
          // 提取子域名部分：api.duo123.net -> api
          const prefix = license.fullDomain.replace('.' + license.domain, '');
          if (prefix && prefix !== license.domain) {
            subdomain = prefix;
          }
        }
      } else if (license.fullDomain.includes('.' + license.domain + '.')) {
        // 处理嵌套情况（不太常见）
        const parts = license.fullDomain.split('.');
        const domainParts = license.domain.split('.');
        if (parts.length > domainParts.length) {
          const prefixParts = parts.slice(0, parts.length - domainParts.length);
          subdomain = prefixParts.join('.');
        }
      }
    }
    
    domainForm.setFieldsValue({
      domain: domain,
      subdomain: subdomain || undefined, // 空字符串转为 undefined
      expiresAt: license.expiresAt ? dayjs(license.expiresAt) : null,
      ipWhitelist: ipWhitelist.length > 0 ? JSON.stringify(ipWhitelist, null, 2) : '',
      notes: license.notes
    });
    setDomainModalVisible(true);
  };

  // 提交域名授权
  const handleDomainSubmit = async (values: any) => {
    if (!selectedAgent) return;

    try {
      // 处理 IP 白名单
      let ipWhitelist = null;
      if (values.ipWhitelist && values.ipWhitelist.trim()) {
        try {
          const parsed = JSON.parse(values.ipWhitelist);
          if (Array.isArray(parsed)) {
            ipWhitelist = parsed;
          }
        } catch (error) {
          message.error('IP白名单格式错误，应为JSON数组格式');
          return;
        }
      }

      const data = {
        ...values,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
        ipWhitelist: ipWhitelist
      };

      if (selectedLicense) {
        // 更新
        await apiClient.put(`/admin/agents/domains/${selectedLicense.id}`, data);
        message.success('域名授权更新成功');
      } else {
        // 创建
        await apiClient.post(`/admin/agents/${selectedAgent.id}/domains`, data);
        message.success('域名授权添加成功');
      }

      setDomainModalVisible(false);
      setSelectedAgent(null);
      setSelectedLicense(null);
      domainForm.resetFields();
      loadAgents();
    } catch (error: any) {
      message.error('操作失败: ' + (error.message || '未知错误'));
    }
  };

  // 重置域名授权密钥
  const handleResetDomainLicenseKey = async (licenseId: string) => {
    try {
      const res = await apiClient.post(`/admin/agents/domains/${licenseId}/reset-license-key`);
      if (res?.success) {
        message.success('授权密钥已重置: ' + res.data.licenseKey);
        loadAgents();
      } else {
        message.error('重置失败: ' + (res?.message || '未知错误'));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          '重置失败，请检查网络连接或联系管理员';
      message.error('重置失败: ' + errorMessage);
      console.error('重置域名授权密钥失败:', error);
    }
  };

  // 删除域名授权
  const handleDeleteDomain = async (licenseId: string) => {
    try {
      const res = await apiClient.delete(`/admin/agents/domains/${licenseId}`);
      if (res?.success) {
        message.success('域名授权删除成功');
        loadAgents();
      } else {
        message.error('删除失败: ' + (res?.message || '未知错误'));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          '删除失败，请检查网络连接或联系管理员';
      message.error('删除失败: ' + errorMessage);
      console.error('删除域名授权失败:', error);
    }
  };

  // 代理商表格列
  const agentColumns = [
    {
      title: '代理商 / 联系人',
      key: 'agentInfo',
      render: (_: any, record: Agent) => (
        <div>
          <Space size="small">
            <TeamOutlined />
            <strong>{record.name}</strong>
            <Tag color="blue">{record.code}</Tag>
          </Space>
          <div className="mt-1 text-xs text-gray-500 space-x-2">
            {record.contactName && <span>{record.contactName}</span>}
            {record.contactPhone && <span>{record.contactPhone}</span>}
            {record.contactEmail && <span>{record.contactEmail}</span>}
          </div>
        </div>
      )
    },
    {
      title: '状态 / 到期时间',
      key: 'status',
      render: (_: any, record: Agent) => {
        const statusLabels: Record<string, string> = {
          ACTIVE: '激活',
          SUSPENDED: '暂停',
          EXPIRED: '过期'
        };

        return (
          <div className="text-gray-700">
            {(statusLabels[record.status] || record.status)}/{record.expiresAt ? dayjs(record.expiresAt).format('YYYY-MM-DD') : '永久'}
          </div>
        );
      }
    },
    {
      title: '授权密钥',
      dataIndex: 'licenseKey',
      key: 'licenseKey',
      width: 250,
      render: (text: string, record: Agent) => (
        <Space>
          <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
            {text}
          </code>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(text, '代理商授权密钥')}
            title="复制密钥"
          />
        </Space>
      )
    },
    {
      title: '域名授权',
      key: 'domains',
      render: (_: any, record: Agent) => (
        <div>
          <Tag color="blue">
            {record._count?.domainLicenses || record.domainLicenses?.length || 0} / {record.maxDomains}
          </Tag>
        </div>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_: any, record: Agent) => (
        <div className="flex flex-col gap-1">
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<GlobalOutlined />}
              onClick={() => handleAddDomain(record)}
              disabled={record._count?.domainLicenses >= record.maxDomains}
            >
              添加域名
            </Button>
            <Button
              type="link"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => handleResetLicenseKey(record.id)}
            >
              重置密钥
            </Button>
          </Space>
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedAgent(record);
                agentForm.setFieldsValue({
                  ...record,
                  expiresAt: record.expiresAt ? dayjs(record.expiresAt) : null
                });
                setAgentModalVisible(true);
              }}
            >
              编辑
            </Button>
            <Popconfirm
              title="确定删除此代理商吗？"
              description={
                record._count?.domainLicenses || record.domainLicenses?.length
                  ? `删除后，所有关联的 ${record._count?.domainLicenses || record.domainLicenses?.length || 0} 个域名授权也将被删除，此操作不可恢复！`
                  : '删除后，此操作不可恢复！'
              }
              onConfirm={() => handleDeleteAgent(record.id)}
              okText="确定删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        </div>
      )
    }
  ];

  // 展开行显示域名授权列表
  const expandedRowRender = (agent: Agent) => {
    const licenses = agent.domainLicenses || [];

    if (licenses.length === 0) {
      return (
        <div className="p-4 text-center text-gray-400">
          暂无域名授权
        </div>
      );
    }

    const statusMeta: Record<string, { color: string; label: string }> = {
      ACTIVE: { color: 'green', label: '激活' },
      SUSPENDED: { color: 'orange', label: '暂停' },
      EXPIRED: { color: 'red', label: '过期' }
    };

    const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
      <div className="flex items-start sm:items-center gap-2 text-sm flex-wrap">
        <span className="text-gray-500 min-w-[72px]">{label}</span>
        <div className="flex items-center gap-2 flex-wrap text-gray-700">
          {children}
        </div>
      </div>
    );

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {licenses.map((license) => {
          const status = statusMeta[license.status] || { color: 'default', label: license.status };
          let whitelistDisplay = '无限制';
          if (license.ipWhitelist) {
            try {
              const ips = JSON.parse(license.ipWhitelist);
              if (Array.isArray(ips) && ips.length > 0) {
                whitelistDisplay = ips.join('、');
              }
            } catch {
              whitelistDisplay = license.ipWhitelist;
            }
          }

          return (
            <Card
              key={license.id}
              size="small"
              className="border border-gray-100 shadow-sm rounded-lg"
              bodyStyle={{ padding: 16 }}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <GlobalOutlined className="text-blue-500" />
                  <span className="font-semibold text-gray-800">{license.fullDomain}</span>
                </div>
                <Space size="small" wrap>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditDomain(license, agent)}
                  >
                    编辑
                  </Button>
                  {!license.licenseKey && (
                    <Button
                      type="link"
                      size="small"
                      icon={<KeyOutlined />}
                      onClick={() => handleResetDomainLicenseKey(license.id)}
                    >
                      生成密钥
                    </Button>
                  )}
                  <Popconfirm
                    title="确定删除此域名授权吗？"
                    onConfirm={() => handleDeleteDomain(license.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              </div>

              <div className="mt-2 space-y-2 text-sm text-gray-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-gray-500">状态 / 到期 / IP</span>
                  <div className="flex items-center gap-2 text-left">
                    <Tag color={status.color}>{status.label}</Tag>
                    <span>/</span>
                    <span>{license.expiresAt ? dayjs(license.expiresAt).format('YYYY-MM-DD') : '永久'}</span>
                    <span>/</span>
                    <span>{whitelistDisplay}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div className="flex items-center gap-2 text-left">
                    <span className="text-gray-500">授权密钥：</span>
                    {license.licenseKey ? (
                      <>
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                          {license.licenseKey}
                        </code>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(license.licenseKey, '域名授权密钥')}
                        >
                          复制
                        </Button>
                      </>
                    ) : (
                      <Tag color="orange">未生成</Tag>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-gray-700 text-left">
                    <span className="text-gray-500">最后验证：</span>
                    <span>{license.lastVerifiedAt ? dayjs(license.lastVerifiedAt).format('YYYY-MM-DD HH:mm') : '-'}</span>
                  </div>
                </div>

                {license.notes && (
                  <div className="flex flex-wrap items-start gap-2">
                    <span className="text-gray-500 min-w-[72px]">备注</span>
                    <span className="text-gray-600">{license.notes}</span>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </Space>
    );
  };

  return (
    <div className="p-6">
      <Card
        title={
          <Space>
            <TeamOutlined />
            <span>代理商管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadAgents}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedAgent(null);
                agentForm.resetFields();
                setAgentModalVisible(true);
              }}
            >
              添加代理商
            </Button>
          </Space>
        }
      >
        <Table
          columns={agentColumns}
          dataSource={agents}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender,
            expandRowByClick: false
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个代理商`
          }}
        />
      </Card>

      {/* 代理商编辑模态框 */}
      <Modal
        title={selectedAgent ? '编辑代理商' : '添加代理商'}
        open={agentModalVisible}
        onCancel={() => {
          setAgentModalVisible(false);
          setSelectedAgent(null);
          agentForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={agentForm}
          layout="vertical"
          onFinish={handleAgentSubmit}
        >
          <Form.Item
            name="name"
            label="代理商名称"
            rules={[{ required: true, message: '请输入代理商名称' }]}
          >
            <Input placeholder="请输入代理商名称" />
          </Form.Item>

          <Form.Item
            name="code"
            label="代理商代码"
            rules={[{ required: true, message: '请输入代理商代码' }]}
            help="唯一标识，用于区分不同代理商"
          >
            <Input placeholder="例如：AGENT001" />
          </Form.Item>

          <Form.Item name="contactName" label="联系人姓名">
            <Input placeholder="联系人姓名" />
          </Form.Item>

          <Form.Item name="contactPhone" label="联系电话">
            <Input placeholder="联系电话" />
          </Form.Item>

          <Form.Item name="contactEmail" label="联系邮箱">
            <Input type="email" placeholder="联系邮箱" />
          </Form.Item>

          <Form.Item name="company" label="公司名称">
            <Input placeholder="公司名称" />
          </Form.Item>

          <Form.Item name="address" label="地址">
            <Input placeholder="地址" />
          </Form.Item>

          <Form.Item
            name="maxDomains"
            label="最大域名数量"
            rules={[{ required: true, message: '请输入最大域名数量' }]}
            initialValue={1}
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="expiresAt" label="授权到期时间">
            <DatePicker
              style={{ width: '100%' }}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="留空表示永久有效"
            />
          </Form.Item>

          <Form.Item name="status" label="状态" initialValue="ACTIVE">
            <Select>
              <Option value="ACTIVE">激活</Option>
              <Option value="SUSPENDED">暂停</Option>
              <Option value="EXPIRED">过期</Option>
            </Select>
          </Form.Item>

          {selectedAgent && selectedAgent.licenseKey && (
            <Form.Item label="授权密钥">
              <Space>
                <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                  {selectedAgent.licenseKey}
                </code>
                <Button
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(selectedAgent.licenseKey, '代理商授权密钥')}
                >
                  复制
                </Button>
              </Space>
              <div className="text-xs text-gray-500 mt-1">
                用于标识和验证代理商身份，可用于客户端验证
              </div>
            </Form.Item>
          )}

          <Form.Item name="notes" label="备注">
            <TextArea rows={3} placeholder="备注信息" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
              <Button onClick={() => {
                setAgentModalVisible(false);
                setSelectedAgent(null);
                agentForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 域名授权编辑模态框 */}
      <Modal
        title={selectedLicense ? '编辑域名授权' : '添加域名授权'}
        open={domainModalVisible}
        onCancel={() => {
          setDomainModalVisible(false);
          setSelectedAgent(null);
          setSelectedLicense(null);
          domainForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedAgent && (
          <div className="mb-4 p-3 bg-blue-50 rounded">
            <strong>代理商：</strong>{selectedAgent.name} ({selectedAgent.code})
            <div className="text-sm text-gray-500 mt-1">
              已使用：{selectedAgent._count?.domainLicenses || selectedAgent.domainLicenses?.length || 0} / {selectedAgent.maxDomains}
            </div>
          </div>
        )}

        <Form
          form={domainForm}
          layout="vertical"
          onFinish={handleDomainSubmit}
        >
          <Form.Item
            name="domain"
            label="域名"
            rules={[{ required: true, message: '请输入域名' }]}
            help="例如：example.com"
          >
            <Input placeholder="example.com" />
          </Form.Item>

          <Form.Item
            name="subdomain"
            label="子域名（可选）"
            help="例如：www, api, app。留空表示主域名"
          >
            <Input placeholder="www" />
          </Form.Item>

          <Form.Item
            name="ipWhitelist"
            label="IP白名单（可选）"
            help="JSON数组格式，如：['192.168.1.1', '10.0.0.0/24']。留空表示不限制IP"
          >
            <Input.TextArea
              rows={3}
              placeholder='["192.168.1.1", "10.0.0.0/24"]'
            />
          </Form.Item>

          <Form.Item name="expiresAt" label="授权到期时间">
            <DatePicker
              style={{ width: '100%' }}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="留空表示永久有效"
            />
          </Form.Item>

          <Form.Item name="status" label="状态" initialValue="ACTIVE">
            <Select>
              <Option value="ACTIVE">激活</Option>
              <Option value="SUSPENDED">暂停</Option>
              <Option value="EXPIRED">过期</Option>
            </Select>
          </Form.Item>

          {selectedLicense && selectedLicense.licenseKey && (
            <Form.Item label="授权密钥">
              <Space>
                <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                  {selectedLicense.licenseKey}
                </code>
                <Button
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(selectedLicense.licenseKey, '域名授权密钥')}
                >
                  复制
                </Button>
              </Space>
              <div className="text-xs text-gray-500 mt-1">
                用于客户端验证域名授权，可用于部署时验证
              </div>
            </Form.Item>
          )}

          <Form.Item name="notes" label="备注">
            <TextArea rows={3} placeholder="备注信息" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
              <Button onClick={() => {
                setDomainModalVisible(false);
                setSelectedAgent(null);
                setSelectedLicense(null);
                domainForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentManagement;

